// Chart.js plugins are registered globally in src/chartjs-init.ts
// and configured here via options.plugins.

import { FC, useRef, useEffect, useCallback, useMemo, useState, type ChangeEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Color,
  Interaction,
  Plugin,
  ScriptableScaleContext,
  TooltipItem,
} from "chart.js";
import type { Chart } from "chart.js";
import { object, number } from "yup";

import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from "@mui/material";
import { RotateLeft, Remove, RotateRight, CommitOutlined, FilterAltOutlined } from "@mui/icons-material";

import { FormDialog } from "@framework/mui-dialog-form";
import { TextInput } from "@framework/mui-inputs-core";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import {
  ExternalTooltip,
  findOrbitDatasetIndexAtCanvasPoint,
  formatExternalTooltipHeaderText,
  Interpolate,
  selectOrbitPathPoints,
  type IExternalTooltipContent,
  type IViewportZoomBounds,
} from "../../../../../../../../components/common/chart-plugins";
import {
  rotationDirectionIconSx,
  StyledWrapper,
  StyledChartContainer,
  StyledChartWithLegendContainer,
  StyledLegendRightContainer,
} from "./styled";
import { downloadDiagram } from "../../../../../../../../utils/download-diagram";

import { IChartDataOrbit, IOrbitItem, TRAIN_ROTATION_DIRECTION } from "../../interfaces";
import { lineColors } from "../../constants";
import { cartesianToPolar } from "../../mappers/orbitMappers";
import { getOrbitDftFrequencyBoundsHz, isValidOrbitBandpassHz } from "../../utils/orbit-harmonics";
import { filterOrbitSamples, ORBIT_ORIGINAL_FORM_COLOR } from "../../utils/orbit-sample-filter";
import { estimateOrbitSamplingRateHz } from "../../utils/orbit-sampling-rate";
import { calcDesktopOrbitComponentRows, ORBIT_DESKTOP_COMP_LIST_PERCENT } from "../../utils/orbit-desktop-components";

import { themeProps } from "../../../../../../../../components/theme";
import { ChartZoomOverview } from "../chart-zoom-overview";
import { CHART_SELECTION_EVENTS, IChartZoomControlProps, applyZoomBounds, useChartZoom } from "../chart-zoom";
import { formatMeasurementNumber } from "../../../../utils/measurement-format";
import { getChartExportFileName, IChartExportControlProps } from "../chart-export";
import { formatAmplitudePhaseTooltipContent } from "../../utils/amplitude-phase-tooltip";
// @ts-ignore
Interaction.modes.index = Interpolate;

const getFontFamily = () =>
  typeof themeProps?.options?.typography === "function"
    ? "Roboto"
    : themeProps?.options?.typography?.fontFamily ?? "Roboto";

const DEGREE_SYMBOL = "\u00B0";

const POLAR_TREND_LEGEND_CONTAINER_ID = "legend-container-polar-trend";

const ORBIT_POLAR_TOOLTIP_ENABLED = true;

const SENSOR_MARKER_OVERLAP_DEGREES = 5;

const getCircularAngleDifference = (leftAngle: number, rightAngle: number): number =>
  Math.abs(((((leftAngle - rightAngle) % 360) + 540) % 360) - 180);

const isSensorMarkerAngleOverlap = (degree: number, markerAngle: number): boolean =>
  getCircularAngleDifference(degree, markerAngle) <= SENSOR_MARKER_OVERLAP_DEGREES;

const getPolarChartDegreeByIndex = (index: number): number => (359 - index + 360) % 360;

interface IOrbitSensorMarker {
  label: "X" | "Y";
  angle: number;
}

interface IOrbitChartDataWithSensors {
  _sensorMarkers?: IOrbitSensorMarker[];
}

const orbitSensorMarkersPlugin: Plugin = {
  id: "orbitSensorMarkers",
  afterDraw: chart => {
    const markers = (chart.data as IOrbitChartDataWithSensors)._sensorMarkers;

    if (!markers?.length) {
      return;
    }

    const { ctx, chartArea } = chart;
    const radialScale = chart.scales.r as { xCenter?: number; yCenter?: number; drawingArea?: number } | undefined;
    const centerX = radialScale?.xCenter ?? (chartArea.left + chartArea.right) / 2;
    const centerY = radialScale?.yCenter ?? (chartArea.top + chartArea.bottom) / 2;
    const radius = radialScale?.drawingArea ?? Math.max(0, Math.min(chartArea.width, chartArea.height) / 2 - 12);
    const markerSize = 18;

    ctx.save();
    ctx.font = `600 11px ${getFontFamily()}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = 1.5;

    markers.forEach(({ label, angle }) => {
      const radians = (angle * Math.PI) / 180;
      const directionX = Math.cos(radians);
      const directionY = -Math.sin(radians);
      const markerRadius = radius + 26;
      const x = centerX + directionX * markerRadius;
      const y = centerY + directionY * markerRadius;

      ctx.beginPath();
      ctx.moveTo(x - directionX * markerSize * 0.5, y - directionY * markerSize * 0.5);
      ctx.lineTo(x - directionX * markerSize, y - directionY * markerSize);
      ctx.strokeStyle = "rgba(51, 65, 85, 0.75)";
      ctx.stroke();

      ctx.fillStyle = "rgba(255, 255, 255, 0.96)";
      ctx.strokeStyle = "rgba(51, 65, 85, 0.9)";
      ctx.fillRect(x - markerSize / 2, y - markerSize / 2, markerSize, markerSize);
      ctx.strokeRect(x - markerSize / 2, y - markerSize / 2, markerSize, markerSize);
      ctx.fillStyle = "rgba(30, 41, 59, 0.95)";
      ctx.fillText(label, x, y + 0.5);
    });

    ctx.restore();
  },
};

interface IOrbitTooltipDatasetMeta {
  _itemsWithDate?: IOrbitItem[];
  _activeOrbitTooltipItem?: IOrbitItem | null;
  unitCode?: number | null;
  units?: string;
}

const getFlatViewportZoomScale = (viewport: IViewportZoomBounds | undefined): number => {
  if (!viewport) {
    return 1;
  }
  const viewportSpan = Math.max(viewport.right - viewport.left, viewport.bottom - viewport.top);

  if (!Number.isFinite(viewportSpan) || viewportSpan <= 0) {
    return 1;
  }

  return 1 / viewportSpan;
};

const scaleFlatZoomMetric = (value: number, viewportScale: number): number => value / viewportScale;

const sortPolarPathPoints = (items: IOrbitItem[]): IOrbitItem[] => {
  if (!items.length) {
    return items;
  }
  const allHaveDate = items.every(item => item.date);
  if (!allHaveDate) {
    return items;
  }
  return [...items].sort((a, b) => a.date!.getTime() - b.date!.getTime());
};

const buildCircleDataset = (label: string) => ({
  label,
  data: Array.from({ length: 181 }, (_, index) => {
    const angle = (index / 180) * 2 * Math.PI;
    return {
      x: Number(Math.cos(angle).toFixed(4)),
      y: Number(Math.sin(angle).toFixed(4)),
    };
  }),
  backgroundColor: "rgba(0,0,0,0)",
  borderColor: "rgba(33, 33, 33, 0.92)",
  borderWidth: 2,
  borderDash: [8, 5],
  pointRadius: 0,
  pointHoverRadius: 0,
  fill: false,
});

const applyRadialClearanceToPolarItems = (items: IOrbitItem[], clearance: number | null): IOrbitItem[] => {
  if (!clearance || clearance <= 0) {
    return items;
  }
  return items.map(item => ({
    ...item,
    x: Number((item.x / clearance).toFixed(4)),
    y: Number((item.y / clearance).toFixed(4)),
    r: item.r / clearance,
  }));
};

const buildPolarOrbitDataset = (
  orbitItem: IChartDataOrbit,
  polarItems: IOrbitItem[],
  strokeColor: string,
  viewportScale: number,
) => {
  const points: number[] = new Array(360).fill(0);
  polarItems.forEach(item => {
    const theta = Math.round(item.theta) % 360;
    points[theta] = item.r;
  });
  const pathPoints = sortPolarPathPoints(polarItems);
  const hitIndexSet = new Set<number>();
  polarItems.forEach(item => {
    const theta = Math.round(item.theta) % 360;
    hitIndexSet.add(359 - theta);
  });

  return {
    label: orbitItem.name,
    data: points.slice().reverse(),
    _itemsWithDate: polarItems,
    _orbitPathPoints: pathPoints,
    orbitStrokeColor: strokeColor,
    backgroundColor: "rgba(0,0,0,0)",
    borderColor: "rgba(0,0,0,0)",
    borderWidth: 0,
    pointBorderColor: strokeColor,
    pointBackgroundColor: strokeColor,
    pointRadius: 0,
    pointHoverRadius: 0,
    pointHitRadius: (ctx: { dataIndex: number; dataset: { _hitIndices?: number[] } }) =>
      ctx.dataset._hitIndices?.includes(ctx.dataIndex) ? scaleFlatZoomMetric(8, viewportScale) : 0,
    pointStyle: "circle",
    pointBorderWidth: 0,
    pointHoverBorderWidth: 0,
    _hitIndices: Array.from(hitIndexSet),
    _seriesKey: orbitItem.seriesKey,
    fill: false,
    unitCode: orbitItem.unitCode ?? null,
    units: orbitItem.units,
    parameterName: orbitItem.parameterLabel ?? orbitItem.parameterName ?? orbitItem.name,
  };
};

const orderOrbitDatasetsBySeries = <T extends { _seriesKey?: string }>(
  datasets: T[],
  frontSeriesKey: string | null,
): T[] => {
  if (!frontSeriesKey) {
    return datasets;
  }

  const behind = datasets.filter(dataset => dataset._seriesKey !== frontSeriesKey);
  const front = datasets.filter(dataset => dataset._seriesKey === frontSeriesKey);

  return front.length ? [...behind, ...front] : datasets;
};

const buildRadarErCircleDataset = (label: string, viewportScale = 1) => ({
  label,
  data: new Array(360).fill(1).slice().reverse(),
  backgroundColor: "rgba(0,0,0,0)",
  borderColor: "rgba(33, 33, 33, 0.92)",
  borderWidth: scaleFlatZoomMetric(2, viewportScale),
  borderDash: [scaleFlatZoomMetric(8, viewportScale), scaleFlatZoomMetric(5, viewportScale)],
  pointRadius: 0,
  pointHoverRadius: 0,
  fill: false,
});

const buildCartesianOrbitScale = (
  axis: "x" | "y",
  orbitExtent: number,
  units: string,
  eccentricityMode: boolean,
  zoomBounds?: { min: number; max: number },
) =>
  applyZoomBounds(
    {
      type: "linear",
      min: -orbitExtent,
      max: orbitExtent,
      ...(axis === "x" ? { position: "bottom" as const } : {}),
      title: {
        display: true,
        text: eccentricityMode ? `ER ${axis.toUpperCase()}` : `${axis.toUpperCase()}, ${units}`,
        font: {
          size: 12,
          family: getFontFamily(),
        },
        color: "rgba(0, 0, 0, 0.87)",
      },
      ticks: {
        font: {
          size: 11,
          family: getFontFamily(),
        },
      },
      grid: {
        color: "#eee",
      },
    },
    zoomBounds,
  );

export enum RotationDirection {
  COUNTER_CLOCKWISE = "ccw",
  UNDEFINED = "none",
  CLOCKWISE = "cw",
}

const mapTrainRotationDirection = (value: TRAIN_ROTATION_DIRECTION | null): RotationDirection | null => {
  switch (value) {
    case TRAIN_ROTATION_DIRECTION.COUNTER_CLOCKWISE:
      return RotationDirection.COUNTER_CLOCKWISE;
    case TRAIN_ROTATION_DIRECTION.CLOCKWISE:
      return RotationDirection.CLOCKWISE;
    default:
      return null;
  }
};

export interface IMultilineChartOrbit extends IChartZoomControlProps, IChartExportControlProps {
  data: IChartDataOrbit[];
  chartTitle: string;
  isPolarTrendChecked: boolean;
  polarTrendMode?: boolean;
  trainRotationDirection?: TRAIN_ROTATION_DIRECTION | null;
  tooltipInHeader?: boolean;
  onHeaderTooltipChange?: (text: string | null) => void;
  frontSeriesKey?: string | null;
  onSeriesClick?: (seriesKey: string) => void;
  onMiddleClick?: () => void;
  externalControls?: {
    hideInlineControls?: boolean;
    rotationDirection?: RotationDirection | null;
    showOrbitPoints?: boolean;
    eccentricityMode?: boolean;
    orbitFilterOpen?: boolean;
    radialClearance?: number | null;
    appliedOrbitBins?: readonly number[];
    appliedBandpassHz?: { lo: number; hi: number } | null;
    showOriginalForm?: boolean;
    onEccentricityModeChange?: (enabled: boolean) => void;
    onOrbitFilterOpenChange?: (open: boolean) => void;
    onRadialClearanceChange?: (value: number | null) => void;
    onAppliedOrbitBinsChange?: (bins: readonly number[]) => void;
    onAppliedBandpassHzChange?: (value: { lo: number; hi: number } | null) => void;
    onShowOriginalFormChange?: (enabled: boolean) => void;
  };
}

export const validationSchema = object().shape({
  radialClearance: number()
    .typeError("form.validations.radialClearanceRange")
    .required("form.validations.radialClearanceRange")
    .min(1, "form.validations.radialClearanceRange")
    .max(99999, "form.validations.radialClearanceRange"),
});

function formatBandpassFreqHz(v: number): string {
  if (!Number.isFinite(v)) {
    return "";
  }
  const rounded = Math.round(v);
  const intTol = 1e-6 * Math.max(1, Math.abs(v));
  if (Math.abs(v - rounded) <= intTol) {
    return String(rounded);
  }
  const a = Math.abs(v);
  if (a >= 1000) {
    return v.toFixed(1);
  }
  if (a >= 100) {
    return v.toFixed(2);
  }
  if (a >= 10) {
    return v.toFixed(3);
  }
  return v.toFixed(4);
}

function parseBandpassHzInput(raw: string): number | null {
  const t = raw.trim().replace(",", ".");
  if (t === "") {
    return null;
  }
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}

export const MultilineChartOrbit: FC<IMultilineChartOrbit> = props => {
  const {
    data,
    chartTitle,
    isPolarTrendChecked,
    polarTrendMode = false,
    trainRotationDirection = null,
    tooltipInHeader = false,
    onHeaderTooltipChange,
    frontSeriesKey = null,
    onSeriesClick,
    onMiddleClick,
    externalControls,
    registerReset,
    registerExport,
    onZoomStateChange,
  } = props;

  const orbitSeries = useMemo(
    () => (Array.isArray(data) ? data.filter((entry): entry is IChartDataOrbit => entry != null) : []),
    [data],
  );
  const lockedRotationDirection = useMemo(
    () => mapTrainRotationDirection(trainRotationDirection),
    [trainRotationDirection],
  );
  const isRotationDirectionLocked = lockedRotationDirection !== null;
  const initialRotationDirection = lockedRotationDirection ?? RotationDirection.UNDEFINED;
  const {
    hideInlineControls = false,
    rotationDirection: externalRotationDirection,
    showOrbitPoints: externalShowOrbitPoints,
    eccentricityMode: externalEccentricityMode,
    orbitFilterOpen: externalOrbitFilterOpen,
    radialClearance: externalRadialClearance,
    appliedOrbitBins: externalAppliedOrbitBins,
    appliedBandpassHz: externalAppliedBandpassHz,
    showOriginalForm: externalShowOriginalForm = false,
    onEccentricityModeChange,
    onOrbitFilterOpenChange,
    onRadialClearanceChange,
    onAppliedOrbitBinsChange,
    onAppliedBandpassHzChange,
    onShowOriginalFormChange,
  } = externalControls ?? {};
  const useFullContainerChartSize = isPolarTrendChecked && (polarTrendMode || hideInlineControls);

  const orbitSettingsControlled = Boolean(
    onRadialClearanceChange || onAppliedOrbitBinsChange || onAppliedBandpassHzChange,
  );

  const canvasRef2 = useRef<HTMLCanvasElement | null>(null);
  const chartRef2 = useRef<ChartJS<any> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartCenteringAreaRef = useRef<HTMLDivElement>(null);
  const orbitInfoRef = useRef<HTMLDivElement>(null);

  const [rotationDirection, setRotationDirection] = useState<RotationDirection>(initialRotationDirection);
  const [showOrbitPoints, setShowOrbitPoints] = useState<boolean>(false);
  const [eccentricityMode, setEccentricityMode] = useState<boolean>(false);
  const [localRadialClearance, setLocalRadialClearance] = useState<number | null>(null);
  const [isRadialClearanceDialogOpen, setIsRadialClearanceDialogOpen] = useState<boolean>(false);
  const chartViewportRef = useRef<HTMLDivElement | null>(null);
  const lastChartSizeRef = useRef({ width: 0, height: 0 });
  const [orbitInfoReservedWidth, setOrbitInfoReservedWidth] = useState(0);
  const [orbitFilterOpen, setOrbitFilterOpen] = useState(false);
  const [localAppliedOrbitBins, setLocalAppliedOrbitBins] = useState<number[]>([]);
  const [draftOrbitBins, setDraftOrbitBins] = useState<number[]>([]);
  const [localAppliedBandpassHz, setLocalAppliedBandpassHz] = useState<{ lo: number; hi: number } | null>(null);
  const [draftBandpassEnabled, setDraftBandpassEnabled] = useState(false);
  const [draftBandpassLo, setDraftBandpassLo] = useState("");
  const [draftBandpassHi, setDraftBandpassHi] = useState("");
  const [draftShowOriginalForm, setDraftShowOriginalForm] = useState(false);
  const [orbitFilterApplyError, setOrbitFilterApplyError] = useState<string | null>(null);
  const [radialClearanceDialogKey, setRadialClearanceDialogKey] = useState(0);
  const previousOrbitMeasureKeyRef = useRef<string | null>(null);
  const radialClearance = orbitSettingsControlled ? externalRadialClearance ?? null : localRadialClearance;
  const appliedOrbitBins = orbitSettingsControlled ? [...(externalAppliedOrbitBins ?? [])] : localAppliedOrbitBins;
  const appliedOrbitBinsKey = appliedOrbitBins.join(",");
  const appliedBandpassHz = orbitSettingsControlled ? externalAppliedBandpassHz ?? null : localAppliedBandpassHz;

  const updateRadialClearance = useCallback(
    (value: number | null) => {
      if (onRadialClearanceChange) {
        onRadialClearanceChange(value);
        return;
      }

      setLocalRadialClearance(value);
    },
    [onRadialClearanceChange],
  );

  const updateAppliedOrbitBins = useCallback(
    (bins: readonly number[]) => {
      if (onAppliedOrbitBinsChange) {
        onAppliedOrbitBinsChange(bins);
        return;
      }

      setLocalAppliedOrbitBins([...bins]);
    },
    [onAppliedOrbitBinsChange],
  );

  const updateAppliedBandpassHz = useCallback(
    (value: { lo: number; hi: number } | null) => {
      if (onAppliedBandpassHzChange) {
        onAppliedBandpassHzChange(value);
        return;
      }

      setLocalAppliedBandpassHz(value);
    },
    [onAppliedBandpassHzChange],
  );

  const clearAppliedOrbitFilters = useCallback(() => {
    updateAppliedOrbitBins([]);
    updateAppliedBandpassHz(null);
  }, [updateAppliedBandpassHz, updateAppliedOrbitBins]);
  const { profile } = useUser<IUser>();

  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { zoomState, applySelectionZoom, clearZoomState, resetZoom, isZoomed, overview, panZoom, overviewPlugin } =
    useChartZoom(
      chartRef2,
      {
        registerReset,
        onZoomStateChange,
      },
      orbitSeries,
    );

  const orbit = orbitSeries[0];
  const param3Num = Number(orbit?.param3);

  const orbitHarmonicFilterDisabledReason = useMemo<"param3" | "sync" | null>(() => {
    if (!Number.isFinite(param3Num) || param3Num <= 0) return "param3";
    if (orbit?.rangeType !== 2) return "sync";
    return null;
  }, [param3Num, orbit?.rangeType]);
  const orbitHarmonicFilterDisabled = orbitHarmonicFilterDisabledReason !== null;

  const orbitSamplingRateHz = useMemo(() => estimateOrbitSamplingRateHz(orbit), [orbit]);
  const orbitBandpassFreqBounds = useMemo(() => {
    const n = orbit?.xData?.length ?? 0;
    if (orbitSamplingRateHz == null || !n) {
      return null;
    }
    return getOrbitDftFrequencyBoundsHz(n, orbitSamplingRateHz);
  }, [orbit?.xData?.length, orbit, orbitSamplingRateHz]);
  const orbitBandpassAvailable = orbitSamplingRateHz != null;
  const orbitFilterButtonDisabled = orbitHarmonicFilterDisabled && !orbitBandpassAvailable;

  const bandpassDraftLoParsed = useMemo(() => parseBandpassHzInput(draftBandpassLo), [draftBandpassLo]);
  const bandpassDraftHiParsed = useMemo(() => parseBandpassHzInput(draftBandpassHi), [draftBandpassHi]);
  const bandpassLoOutOfRange =
    draftBandpassEnabled &&
    orbitBandpassFreqBounds != null &&
    bandpassDraftLoParsed != null &&
    (bandpassDraftLoParsed < orbitBandpassFreqBounds.fMin - 1e-12 ||
      bandpassDraftLoParsed > orbitBandpassFreqBounds.fMax + 1e-12);
  const bandpassHiOutOfRange =
    draftBandpassEnabled &&
    orbitBandpassFreqBounds != null &&
    bandpassDraftHiParsed != null &&
    (bandpassDraftHiParsed < orbitBandpassFreqBounds.fMin - 1e-12 ||
      bandpassDraftHiParsed > orbitBandpassFreqBounds.fMax + 1e-12);

  const orbitFilterTooltip = useMemo(() => {
    if (orbitFilterButtonDisabled) {
      return formatMessage({ id: "pages.scheme.analytics.orbitFilterDisabledFully" });
    }
    if (orbitHarmonicFilterDisabled) {
      return formatMessage({ id: "pages.scheme.analytics.orbitFilterTooltipBandpassOnly" });
    }
    return formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterTooltip" });
  }, [formatMessage, orbitFilterButtonDisabled, orbitHarmonicFilterDisabled]);

  const orbitDesktopComponentRows = useMemo(() => {
    const xd = orbit?.xData;
    const yd = orbit?.yData;
    const p3 = Number(orbit?.param3);
    if (
      !xd?.length ||
      !yd?.length ||
      xd.length !== yd.length ||
      orbit?.rangeType !== 2 ||
      !Number.isFinite(p3) ||
      p3 <= 0
    ) {
      return [];
    }
    return calcDesktopOrbitComponentRows(xd, yd, p3, orbit?.resolution, ORBIT_DESKTOP_COMP_LIST_PERCENT);
  }, [orbit?.xData, orbit?.yData, orbit?.param3, orbit?.rangeType, orbit?.resolution]);

  const getFilteredPolarItems = useCallback(
    (singleOrbit: IChartDataOrbit | undefined) => {
      if (!singleOrbit) {
        return [];
      }

      const filtered = filterOrbitSamples(singleOrbit.xData, singleOrbit.yData, {
        bins: appliedOrbitBins,
        bandpassHz: appliedBandpassHz,
        samplingRateHz: estimateOrbitSamplingRateHz(singleOrbit),
        resolution: singleOrbit.resolution,
        param3: Number(singleOrbit.param3),
      });
      if (!filtered) {
        return singleOrbit.items ?? [];
      }

      return cartesianToPolar(filtered.x, filtered.y, singleOrbit.zeroIndexes ?? []);
    },
    [appliedBandpassHz, appliedOrbitBins],
  );
  const showOriginalOrbit = externalShowOriginalForm && (appliedOrbitBins.length > 0 || appliedBandpassHz != null);

  const orbitPoints = useMemo(() => (orbit ? getFilteredPolarItems(orbit) : []), [orbit, getFilteredPolarItems]);

  const getPolarTooltipItem = useCallback((dataPoint: TooltipItem<"radar">): IOrbitItem | null => {
    const dataset = dataPoint?.dataset as unknown as IOrbitTooltipDatasetMeta | undefined;

    if (!dataset) {
      return null;
    }

    if (dataset._activeOrbitTooltipItem) {
      return dataset._activeOrbitTooltipItem;
    }

    const items = dataset._itemsWithDate;

    if (!items?.length) {
      return null;
    }

    const degree = (359 - dataPoint.dataIndex + 360) % 360;

    for (let index = items.length - 1; index >= 0; index -= 1) {
      const item = items[index];
      if (((Math.round(item.theta) % 360) + 360) % 360 === degree) {
        return item;
      }
    }

    return null;
  }, []);

  const formatPolarTooltipValue = useCallback(
    (value: number, dataPoint: TooltipItem<"radar">) => {
      const dataset = dataPoint?.dataset as unknown as IOrbitTooltipDatasetMeta | undefined;
      return formatMeasurementNumber(value, dataset?.unitCode ?? null, profile.interfaceSettings.dataFormat, {
        fallbackDigits: 2,
      });
    },
    [profile.interfaceSettings.dataFormat],
  );

  const formatPolarAmplitudePhaseTooltip = useCallback(
    (dataPoint: TooltipItem<"radar">) => {
      const item = getPolarTooltipItem(dataPoint);
      const dataset = dataPoint?.dataset as unknown as IOrbitTooltipDatasetMeta | undefined;

      if (!item) {
        return null;
      }

      return formatAmplitudePhaseTooltipContent(
        {
          title: dataPoint.dataset.label ?? "",
          amplitude: item.r,
          amplitudeUnitCode: dataset?.unitCode ?? null,
          amplitudeUnits: dataset?.units ?? "",
          phase: item.theta,
          rotationFrequencyRpm: item.rotationFrequencyRpm,
        },
        {
          dataFormat: profile.interfaceSettings.dataFormat,
          rpmUnitLabel: formatMessage({ id: "pages.scheme.analytics.rotationFrequencyRpmUnit" }),
        },
      );
    },
    [formatMessage, getPolarTooltipItem, profile.interfaceSettings.dataFormat],
  );

  const formatPolarTooltipContent = useCallback(
    (dataPoints: TooltipItem<"radar">[]) => {
      const dataPoint = dataPoints.find(({ raw }) => raw !== 0) ?? dataPoints[0];

      return dataPoint ? formatPolarAmplitudePhaseTooltip(dataPoint) : null;
    },
    [formatPolarAmplitudePhaseTooltip],
  );

  useEffect(() => {
    const orbitMeasureKey = `${orbit?.idPoint ?? ""}:${orbit?.parameterName ?? orbit?.name ?? ""}:${
      orbit?.rangeType ?? ""
    }`;

    if (previousOrbitMeasureKeyRef.current === null) {
      previousOrbitMeasureKeyRef.current = orbitMeasureKey;
      return;
    }

    if (previousOrbitMeasureKeyRef.current === orbitMeasureKey) {
      return;
    }

    previousOrbitMeasureKeyRef.current = orbitMeasureKey;
    setShowOrbitPoints(false);
    clearAppliedOrbitFilters();
  }, [clearAppliedOrbitFilters, orbit?.idPoint, orbit?.name, orbit?.parameterName, orbit?.rangeType]);

  useEffect(() => {
    if (!orbitSettingsControlled || radialClearance === null) {
      return;
    }

    setEccentricityMode(true);
  }, [orbitSettingsControlled, radialClearance]);

  useEffect(() => {
    if (externalRotationDirection === undefined) {
      setRotationDirection(lockedRotationDirection ?? RotationDirection.UNDEFINED);
    }
  }, [externalRotationDirection, lockedRotationDirection]);

  const units = orbit?.units ?? "";
  const maxX = orbit?.Xmax ?? 0;
  const maxY = orbit?.Ymax ?? 0;
  const maxS = orbit?.Smax ?? 0;

  const sensorMarkers = useMemo<IOrbitSensorMarker[]>(() => {
    if (polarTrendMode) {
      return [];
    }

    return [
      { label: "X" as const, angle: orbit?.xPickupAngle },
      { label: "Y" as const, angle: orbit?.yPickupAngle },
    ].flatMap(marker =>
      marker.angle != null && Number.isFinite(marker.angle) ? [{ label: marker.label, angle: marker.angle }] : [],
    );
  }, [orbit?.xPickupAngle, orbit?.yPickupAngle, polarTrendMode]);

  const chartLabels = useMemo(
    () => Array.from({ length: 360 }, (_, i) => (i % 30 === 0 ? `${i}${DEGREE_SYMBOL}` : "")).reverse(),
    [],
  );

  const shouldHidePolarPointLabel = useCallback(
    (label: string, index: number) => {
      if (!label) {
        return true;
      }

      const degree = getPolarChartDegreeByIndex(index);
      return sensorMarkers.some(({ angle }) => isSensorMarkerAngleOverlap(degree, angle));
    },
    [sensorMarkers],
  );

  const maxA = useMemo(() => {
    let max = 0;
    const consider = (items: readonly { r?: number }[]) => {
      for (const item of items) {
        if (typeof item.r === "number") {
          const absR = Math.abs(item.r);
          if (absR > max) max = absR;
        }
      }
    };
    for (const orbitItem of orbitSeries) {
      consider(getFilteredPolarItems(orbitItem));
      if (showOriginalOrbit) {
        consider(orbitItem.items ?? []);
      }
    }
    return max;
  }, [getFilteredPolarItems, orbitSeries, showOriginalOrbit]);

  const cartesianOrbitPoints = useMemo(() => {
    if (!eccentricityMode || !radialClearance || radialClearance <= 0) {
      return orbitPoints;
    }

    return orbitPoints.map(point => ({
      ...point,
      x: Number((point.x / radialClearance).toFixed(4)),
      y: Number((point.y / radialClearance).toFixed(4)),
      r: point.r === 0 ? 0 : Math.min(1, Math.max(0, Math.abs(point.r) / radialClearance)),
    }));
  }, [eccentricityMode, orbitPoints, radialClearance]);

  const radialClearanceFormInitialValues = useMemo(
    () => ({
      radialClearance:
        radialClearance != null && Number.isFinite(radialClearance) && radialClearance > 0
          ? String(radialClearance)
          : "",
    }),
    [radialClearance],
  );

  const orbitExtent = useMemo(() => {
    if (isPolarTrendChecked) {
      if (eccentricityMode && radialClearance && radialClearance > 0) {
        const maxNorm = maxA / radialClearance;
        return Math.max((Number.isFinite(maxNorm) ? maxNorm : 0) * 1.1, 1.1);
      }
      return (maxA || 1) * 1.1;
    }

    if (eccentricityMode && radialClearance && radialClearance > 0) {
      return 1.1;
    }

    const pointsExtent = cartesianOrbitPoints.reduce((maxValue, point) => {
      return Math.max(maxValue, Math.abs(point.x), Math.abs(point.y));
    }, 0);

    const base =
      appliedOrbitBins.length > 0 || appliedBandpassHz != null
        ? Math.max(pointsExtent, 1)
        : Math.max(Math.abs(maxX), Math.abs(maxY), pointsExtent, 1);

    return base * 1.1;
  }, [
    appliedBandpassHz,
    appliedOrbitBins.length,
    cartesianOrbitPoints,
    eccentricityMode,
    isPolarTrendChecked,
    maxA,
    maxX,
    maxY,
    radialClearance,
  ]);

  const polarViewportZoomScale = useMemo(() => {
    const viewport = isPolarTrendChecked ? zoomState?.viewport : undefined;

    return getFlatViewportZoomScale(viewport);
  }, [isPolarTrendChecked, zoomState?.viewport]);
  const polarViewportZoomed = isPolarTrendChecked && zoomState?.viewport != null;

  const chartData = useMemo<ChartData<any, any[], string>>(() => {
    if (isPolarTrendChecked && polarTrendMode) {
      const polarClearance = eccentricityMode && radialClearance && radialClearance > 0 ? radialClearance : null;
      const erCirclePolarTrend =
        polarClearance !== null ? [buildRadarErCircleDataset("ER=1", polarViewportZoomScale)] : [];

      return {
        labels: chartLabels,
        datasets: [
          ...orbitSeries.map((orbitItem, i) => {
            const polarItems = applyRadialClearanceToPolarItems(getFilteredPolarItems(orbitItem), polarClearance);
            const points: number[] = new Array(360).fill(0);
            polarItems.forEach(item => {
              const theta = Math.round(item.theta) % 360;
              points[theta] = item.r;
            });
            const pathPoints = sortPolarPathPoints(polarItems);
            const stroke = lineColors[i % lineColors.length];
            return {
              label: orbitItem.name,
              data: points.slice().reverse(),
              _itemsWithDate: polarItems,
              _orbitPathPoints: pathPoints,
              orbitStrokeColor: stroke,
              borderColor: "rgba(0,0,0,0)",
              backgroundColor: "rgba(0,0,0,0)",
              borderWidth: 0,
              pointBackgroundColor: stroke,
              pointBorderColor: stroke,
              // The orbit plugin draws and hit-tests the original points. Radar markers are angle-binned duplicates.
              pointRadius: 0,
              pointHoverRadius: 0,
              pointHitRadius: 0,
              pointHoverBorderWidth: 0,
              pointBorderWidth: 0,
              pointStyle: "circle" as const,
              fill: false,
              parameterName: orbitItem.parameterLabel ?? orbitItem.parameterName ?? orbitItem.name,
              unitCode: orbitItem.unitCode ?? null,
              units: orbitItem.units,
            };
          }),
          ...erCirclePolarTrend,
        ],
        _polarTrendMode: true,
      };
    }

    if (isPolarTrendChecked) {
      const polarClearance = eccentricityMode && radialClearance && radialClearance > 0 ? radialClearance : null;
      const erCircleOrbit = polarClearance !== null ? [buildRadarErCircleDataset("ER=1", polarViewportZoomScale)] : [];

      return {
        labels: chartLabels,
        datasets: [
          ...orderOrbitDatasetsBySeries(
            orbitSeries.flatMap((orbitItem, i) => {
              const strokeColor = orbitItem.color ?? lineColors[i % lineColors.length];
              const filteredItems = applyRadialClearanceToPolarItems(getFilteredPolarItems(orbitItem), polarClearance);
              const datasets = [];
              if (showOriginalOrbit) {
                datasets.push(
                  buildPolarOrbitDataset(
                    orbitItem,
                    applyRadialClearanceToPolarItems(orbitItem.items ?? [], polarClearance),
                    ORBIT_ORIGINAL_FORM_COLOR,
                    polarViewportZoomScale,
                  ),
                );
              }
              datasets.push(buildPolarOrbitDataset(orbitItem, filteredItems, strokeColor, polarViewportZoomScale));
              return datasets;
            }),
            frontSeriesKey,
          ),
          ...erCircleOrbit,
        ],
        _rotationDirection: rotationDirection,
        _orbitColor: orbit?.color ?? lineColors[0],
        _showOrbitPoints: showOrbitPoints,
        _sensorMarkers: sensorMarkers,
      };
    }

    const erCircleCartesian =
      eccentricityMode && radialClearance && radialClearance > 0 ? [buildCircleDataset("ER=1")] : [];
    const displayCartesianOrbitPoints = selectOrbitPathPoints(cartesianOrbitPoints);
    const datasets = [
      {
        label: orbit?.name ?? "",
        data: displayCartesianOrbitPoints.map(point => ({
          x: point.x,
          y: point.y,
        })),
        backgroundColor: "rgba(0,0,0,0)",
        borderColor: orbit?.color ?? lineColors[0],
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      },
      ...erCircleCartesian,
    ];

    return {
      datasets,
      _rotationDirection: rotationDirection,
      _points: cartesianOrbitPoints,
      _showOrbitPoints: showOrbitPoints,
      _isCartesianOrbit: true,
      _orbitColor: orbit?.color ?? lineColors[0],
      _sensorMarkers: sensorMarkers,
    };
  }, [
    cartesianOrbitPoints,
    chartLabels,
    frontSeriesKey,
    orbitSeries,
    getFilteredPolarItems,
    eccentricityMode,
    isPolarTrendChecked,
    orbit?.color,
    orbit?.name,
    polarTrendMode,
    polarViewportZoomScale,
    radialClearance,
    rotationDirection,
    sensorMarkers,
    showOrbitPoints,
    showOriginalOrbit,
  ]);

  const onSeriesClickRef = useRef(onSeriesClick);
  onSeriesClickRef.current = onSeriesClick;
  const handleOrbitPointClick = useCallback((datasetIndex: number) => {
    const dataset = chartRef2.current?.data.datasets[datasetIndex] as { _seriesKey?: string } | undefined;
    const seriesKey = dataset?._seriesKey;
    if (seriesKey) {
      onSeriesClickRef.current?.(seriesKey);
    }
  }, []);
  const orbitClickViewportRef = useRef(zoomState?.viewport);
  orbitClickViewportRef.current = zoomState?.viewport;

  useEffect(() => {
    const canvas = canvasRef2.current;
    if (!canvas || !onSeriesClick) {
      return undefined;
    }

    const handleCanvasClick = (event: MouseEvent) => {
      const chart = chartRef2.current;
      const selectionZoom = (
        chart as {
          selectionZoom?: { dragging?: boolean; mouseDown?: boolean; suppressClickLock?: number };
        } | null
      )?.selectionZoom;
      if (!chart || selectionZoom?.dragging || selectionZoom?.mouseDown || selectionZoom?.suppressClickLock) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        return;
      }

      const datasetIndex = findOrbitDatasetIndexAtCanvasPoint(
        chart,
        {
          x: (event.clientX - rect.left) * (chart.width / rect.width),
          y: (event.clientY - rect.top) * (chart.height / rect.height),
        },
        { viewport: orbitClickViewportRef.current },
      );
      if (datasetIndex !== null) {
        handleOrbitPointClick(datasetIndex);
      }
    };

    canvas.addEventListener("click", handleCanvasClick);
    return () => canvas.removeEventListener("click", handleCanvasClick);
  }, [handleOrbitPointClick, onSeriesClick]);

  const headerTooltipOptions = useMemo(
    () =>
      tooltipInHeader
        ? {
            displayMode: "header" as const,
            onHeaderContentChange: (content: IExternalTooltipContent | null) => {
              onHeaderTooltipChange?.(content ? formatExternalTooltipHeaderText(content) : null);
            },
          }
        : {},
    [onHeaderTooltipChange, tooltipInHeader],
  );

  const options = useMemo<ChartOptions<any>>(
    () => ({
      animation: false,
      maintainAspectRatio: false,
      responsive: false,
      events: CHART_SELECTION_EVENTS,
      layout:
        isPolarTrendChecked && !polarTrendMode ? { padding: { top: 40, right: 40, bottom: 12, left: 14 } } : undefined,
      scales: isPolarTrendChecked
        ? {
            r: {
              min: 0,
              max: orbitExtent,
              angleLines: {
                display: true,
                color: (context: ScriptableScaleContext) => {
                  const degree = getPolarChartDegreeByIndex(context.index);
                  return degree % 30 === 0 ? "#eee" : "rgba(0,0,0,0)";
                },
                lineWidth: (context: ScriptableScaleContext) => {
                  const degree = getPolarChartDegreeByIndex(context.index);
                  return degree % 30 === 0 ? 1 : 0;
                },
              },
              grid: { color: "#eee" },
              pointLabels: {
                display: !polarViewportZoomed,
                centerPointLabels: true,
                callback: (label: string, index: number) => (shouldHidePolarPointLabel(label, index) ? "" : label),
                font: {
                  size: 10,
                  family: getFontFamily(),
                },
                rotation: 0,
              },
              ticks: {
                display: !polarViewportZoomed,
                stepSize: eccentricityMode ? 0.2 : 5,
                ...(polarTrendMode
                  ? {
                      maxTicksLimit: 10,
                      autoSkip: true,
                    }
                  : {}),
                font: {
                  size: 10,
                  family: getFontFamily(),
                },
              },
              startAngle: 90,
            },
          }
        : {
            x: buildCartesianOrbitScale("x", orbitExtent, units, eccentricityMode, zoomState?.x),
            y: buildCartesianOrbitScale("y", orbitExtent, units, eccentricityMode, zoomState?.y?.y),
          },
      elements: {
        point: {
          radius: 0,
          hoverRadius: 0,
        },
      },
      interaction: {
        intersect: polarTrendMode,
        mode: "nearest" as const,
        axis: "xy",
      },
      plugins: {
        drawOrbitConnectingLines: {
          onPointClick: handleOrbitPointClick,
          viewportScale: isPolarTrendChecked ? polarViewportZoomScale : 1,
          viewport: isPolarTrendChecked ? zoomState?.viewport : undefined,
          viewportAngularScaleLabels: polarViewportZoomed
            ? {
                fontFamily: getFontFamily(),
                fontSize: 14,
                fontWeight: 700,
                padding: 5,
                color: "rgba(17, 24, 39, 0.96)",
                backgroundColor: "rgba(255, 255, 255, 0.98)",
                borderColor: "rgba(17, 24, 39, 0.45)",
                leaderColor: "rgba(17, 24, 39, 0.45)",
              }
            : false,
        },
        alarmLevel: false,
        title: {
          display: false,
          text: chartTitle,
          font: {
            size: 10,
            family: getFontFamily(),
          },
        },
        crosshair: polarTrendMode ? false : isPolarTrendChecked ? undefined : false,
        selectionZoom: {
          enabled: true,
          ...(isPolarTrendChecked
            ? {
                viewportMode: true,
                viewport: zoomState?.viewport ?? undefined,
              }
            : {
                xScaleId: "x",
                yScaleIds: ["y"],
              }),
          minDragPx: 8,
          onZoom: applySelectionZoom,
          onReset: clearZoomState,
        },
        ...(polarTrendMode
          ? {
              htmlLegend: {
                containerId: POLAR_TREND_LEGEND_CONTAINER_ID,
                fontFamily: getFontFamily(),
              },
              legend: {
                display: false,
                labels: {
                  boxWidth: 10,
                  padding: 10,
                  font: {
                    size: 10,
                    family: getFontFamily(),
                  },
                  generateLabels: (chart: Chart<"radar">) => {
                    const { data } = chart;
                    return data.datasets.map((dataset, i: number) => {
                      const stroke =
                        (dataset as unknown as { orbitStrokeColor?: string }).orbitStrokeColor ??
                        (dataset.borderColor as string);
                      const parameterName =
                        (dataset as unknown as { parameterName?: string }).parameterName ?? dataset.label ?? "";
                      const labelText = (dataset.label ?? parameterName) || "";
                      return {
                        text: labelText,
                        parameterName,
                        fillStyle: stroke as Color,
                        strokeStyle: stroke as Color,
                        borderRadius: 6,
                        usePointStyle: true,
                        pointStyle: "circle" as const,
                        lineWidth: chart.isDatasetVisible(i) ? 0 : 1,
                        hidden: !chart.isDatasetVisible(i),
                        datasetIndex: i,
                        onClick: () => {
                          const meta = chart.getDatasetMeta(i);
                          meta.hidden = meta.hidden ?? !chart.data.datasets[i].hidden;
                          chart.update();
                        },
                      };
                    });
                  },
                },
              },
            }
          : { legend: { display: false } }),
        tooltip: polarTrendMode
          ? {
              enabled: false,
              intersect: true,
              mode: "nearest" as const,
              external: ExternalTooltip,
              unitsX: DEGREE_SYMBOL,
              unitsY: orbitSeries[0]?.units,
              yValueFormatter: formatPolarTooltipValue,
              contentFormatter: formatPolarTooltipContent,
              ...headerTooltipOptions,
            }
          : isPolarTrendChecked
            ? ORBIT_POLAR_TOOLTIP_ENABLED
              ? {
                  enabled: false,
                  intersect: true,
                  mode: "nearest" as const,
                  external: ExternalTooltip,
                  unitsX: DEGREE_SYMBOL,
                  unitsY: orbitSeries[0]?.units,
                  yValueFormatter: formatPolarTooltipValue,
                  contentFormatter: formatPolarTooltipContent,
                  ...headerTooltipOptions,
                }
              : { enabled: false }
            : { enabled: false },
      },
    }),
    [
      applySelectionZoom,
      chartTitle,
      clearZoomState,
      orbitSeries,
      eccentricityMode,
      formatPolarTooltipValue,
      formatPolarTooltipContent,
      formatMessage,
      handleOrbitPointClick,
      isPolarTrendChecked,
      polarTrendMode,
      polarViewportZoomScale,
      polarViewportZoomed,
      orbitExtent,
      units,
      hideInlineControls,
      shouldHidePolarPointLabel,
      headerTooltipOptions,
      zoomState?.viewport,
      zoomState?.x,
      zoomState?.y,
    ],
  );

  const destroyChart = useCallback(() => {
    if (chartRef2.current) {
      chartRef2.current.destroy();
      chartRef2.current = null;
    }
  }, []);

  const renderChart = useCallback(() => {
    if (!canvasRef2.current) return;

    if (chartRef2.current) {
      chartRef2.current.destroy();
      chartRef2.current = null;
    }

    lastChartSizeRef.current = { width: 0, height: 0 };

    chartRef2.current = new ChartJS(canvasRef2.current, {
      type: isPolarTrendChecked ? "radar" : "line",
      data: chartData,
      options,
      plugins: [orbitSensorMarkersPlugin, overviewPlugin],
    });
  }, [chartData, isPolarTrendChecked, options, overviewPlugin]);

  const openEccentricityDialog = useCallback(() => {
    // TODO: Read radial clearance from DB when the backend exposes it.
    setRadialClearanceDialogKey(k => k + 1);
    setIsRadialClearanceDialogOpen(true);
  }, []);

  const handleRotationChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, value: RotationDirection | null) => {
      if (isRotationDirectionLocked) {
        return;
      }
      if (value !== null) setRotationDirection(value);
    },
    [isRotationDirectionLocked],
  );

  const handleToggleShowOrbitPoints = useCallback(() => {
    if (showOrbitPoints) {
      setShowOrbitPoints(false);
      return;
    }

    if (orbit?.rangeType !== 2) {
      enqueueSnackbar(formatMessage({ id: "pages.scheme.analytics.orbitMarkerPointsSyncOnly" }), {
        variant: "warning",
      });
      return;
    }

    setShowOrbitPoints(true);
  }, [enqueueSnackbar, formatMessage, orbit?.rangeType, showOrbitPoints]);

  const handleEccentricityToggle = useCallback(() => {
    openEccentricityDialog();
  }, [openEccentricityDialog]);

  const handleRadialClearanceConfirm = useCallback(
    async (values: any) => {
      const val = Number(values?.radialClearance);
      updateRadialClearance(val);
      setIsRadialClearanceDialogOpen(false);
      setEccentricityMode(true);
      onEccentricityModeChange?.(true);

      return Promise.resolve();
    },
    [onEccentricityModeChange, updateRadialClearance],
  );

  const handleRadialClearanceCancel = useCallback(() => {
    setIsRadialClearanceDialogOpen(false);
    setEccentricityMode(false);
    updateRadialClearance(null);
    onEccentricityModeChange?.(false);
  }, [onEccentricityModeChange, updateRadialClearance]);

  const handleOpenOrbitFilter = useCallback(() => {
    setDraftOrbitBins([...appliedOrbitBins].sort((a, b) => a - b));
    setDraftBandpassEnabled(appliedBandpassHz != null);
    setDraftBandpassLo(appliedBandpassHz != null ? String(appliedBandpassHz.lo) : "");
    setDraftBandpassHi(appliedBandpassHz != null ? String(appliedBandpassHz.hi) : "");
    setDraftShowOriginalForm(externalShowOriginalForm);
    setOrbitFilterApplyError(null);
    setOrbitFilterOpen(true);
    onOrbitFilterOpenChange?.(true);
  }, [appliedBandpassHz, appliedOrbitBins, externalShowOriginalForm, onOrbitFilterOpenChange]);

  const handleCloseOrbitFilter = useCallback(() => {
    setOrbitFilterOpen(false);
    setOrbitFilterApplyError(null);
    onOrbitFilterOpenChange?.(false);
  }, [onOrbitFilterOpenChange]);

  const handleApplyOrbitFilter = useCallback(() => {
    const fs = estimateOrbitSamplingRateHz(orbit);
    const n = orbit?.xData?.length;
    if (draftBandpassEnabled) {
      const lo = Number(draftBandpassLo.replace(",", "."));
      const hi = Number(draftBandpassHi.replace(",", "."));
      if (fs == null || !isValidOrbitBandpassHz(fs, lo, hi, n)) {
        setOrbitFilterApplyError(formatMessage({ id: "pages.scheme.analytics.orbitBandpassFilterInvalid" }));
        return;
      }
      updateAppliedBandpassHz({ lo, hi });
      updateAppliedOrbitBins([]);
      onShowOriginalFormChange?.(draftShowOriginalForm);
      setOrbitFilterApplyError(null);
      setOrbitFilterOpen(false);
      onOrbitFilterOpenChange?.(false);
      return;
    }
    updateAppliedBandpassHz(null);
    updateAppliedOrbitBins([...draftOrbitBins]);
    onShowOriginalFormChange?.(draftShowOriginalForm);
    setOrbitFilterApplyError(null);
    setOrbitFilterOpen(false);
    onOrbitFilterOpenChange?.(false);
  }, [
    draftBandpassEnabled,
    draftBandpassHi,
    draftBandpassLo,
    draftOrbitBins,
    draftShowOriginalForm,
    formatMessage,
    onOrbitFilterOpenChange,
    onShowOriginalFormChange,
    orbit,
    updateAppliedBandpassHz,
    updateAppliedOrbitBins,
  ]);

  const handleBandpassLoBlur = useCallback(() => {
    if (!draftBandpassEnabled || !orbitBandpassFreqBounds) {
      return;
    }
    const v = parseBandpassHzInput(draftBandpassLo);
    if (v == null) {
      return;
    }
    const { fMin, fMax } = orbitBandpassFreqBounds;
    const c = Math.min(Math.max(v, fMin), fMax);
    if (c !== v) {
      setDraftBandpassLo(String(c));
    }
  }, [draftBandpassEnabled, draftBandpassLo, orbitBandpassFreqBounds]);

  const handleBandpassHiBlur = useCallback(() => {
    if (!draftBandpassEnabled || !orbitBandpassFreqBounds) {
      return;
    }
    const v = parseBandpassHzInput(draftBandpassHi);
    if (v == null) {
      return;
    }
    const { fMin, fMax } = orbitBandpassFreqBounds;
    const c = Math.min(Math.max(v, fMin), fMax);
    if (c !== v) {
      setDraftBandpassHi(String(c));
    }
  }, [draftBandpassEnabled, draftBandpassHi, orbitBandpassFreqBounds]);

  const handleClearOrbitFilter = useCallback(() => {
    setDraftOrbitBins([]);
    clearAppliedOrbitFilters();
    setDraftBandpassEnabled(false);
    setDraftBandpassLo("");
    setDraftBandpassHi("");
    setDraftShowOriginalForm(false);
    onShowOriginalFormChange?.(false);
    setOrbitFilterApplyError(null);
  }, [clearAppliedOrbitFilters, onShowOriginalFormChange]);

  const handleToggleDraftBin = useCallback((binIndex: number) => {
    setDraftBandpassEnabled(false);
    setDraftOrbitBins(prev =>
      prev.includes(binIndex) ? prev.filter(x => x !== binIndex) : [...prev, binIndex].sort((a, b) => a - b),
    );
  }, []);

  const handleDraftBandpassEnabledChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setDraftBandpassEnabled(checked);
    if (checked) {
      setDraftOrbitBins([]);
    }
  }, []);

  useEffect(() => {
    if (externalRotationDirection !== undefined && externalRotationDirection !== null) {
      if (isRotationDirectionLocked) {
        return;
      }

      setRotationDirection(externalRotationDirection);
    }
  }, [externalRotationDirection, isRotationDirectionLocked]);

  useEffect(() => {
    if (externalShowOrbitPoints !== undefined) {
      setShowOrbitPoints(externalShowOrbitPoints);
    }
  }, [externalShowOrbitPoints]);

  useEffect(() => {
    if (externalEccentricityMode === undefined) {
      return;
    }

    if (!externalEccentricityMode) {
      setIsRadialClearanceDialogOpen(false);
      setEccentricityMode(false);
      updateRadialClearance(null);
      return;
    }

    if (radialClearance !== null) {
      setEccentricityMode(true);
      return;
    }

    openEccentricityDialog();
  }, [externalEccentricityMode, openEccentricityDialog, radialClearance, updateRadialClearance]);

  useEffect(() => {
    if (externalOrbitFilterOpen === undefined) {
      return;
    }

    if (!externalOrbitFilterOpen) {
      if (orbitFilterOpen) {
        handleCloseOrbitFilter();
      }
      return;
    }

    if (orbitFilterButtonDisabled) {
      onOrbitFilterOpenChange?.(false);
      return;
    }

    if (!orbitFilterOpen) {
      handleOpenOrbitFilter();
    }
  }, [
    externalOrbitFilterOpen,
    handleCloseOrbitFilter,
    handleOpenOrbitFilter,
    onOrbitFilterOpenChange,
    orbitFilterButtonDisabled,
    orbitFilterOpen,
  ]);

  const applyChartViewportSize = useCallback(
    (chartWidth: number, chartHeight: number, squareSize: number) => {
      const viewport = chartViewportRef.current;

      if (viewport) {
        if (useFullContainerChartSize) {
          viewport.style.width = "100%";
          viewport.style.height = "100%";
        } else {
          viewport.style.width = `${squareSize}px`;
          viewport.style.height = `${squareSize}px`;
        }
      }

      const chart = chartRef2.current;

      if (!chart || (chart.width === chartWidth && chart.height === chartHeight)) {
        return;
      }

      chart.resize(chartWidth, chartHeight);
    },
    [useFullContainerChartSize],
  );

  const handleResize = useCallback(() => {
    const container = chartContainerRef.current;

    if (!container) {
      return;
    }

    const width = Math.floor(container.clientWidth);
    const height = Math.floor(container.clientHeight);

    if (width <= 0 || height <= 0) {
      return;
    }

    if (lastChartSizeRef.current.width === width && lastChartSizeRef.current.height === height) {
      return;
    }

    lastChartSizeRef.current = { width, height };

    const availableWidth = Math.max(1, width - orbitInfoReservedWidth);
    const nextSquareSize = Math.min(availableWidth, height);
    const chartWidth = useFullContainerChartSize ? availableWidth : nextSquareSize;
    const chartHeight = useFullContainerChartSize ? height : nextSquareSize;

    applyChartViewportSize(chartWidth, chartHeight, nextSquareSize);
  }, [applyChartViewportSize, orbitInfoReservedWidth, useFullContainerChartSize]);

  useEffect(() => {
    const info = orbitInfoRef.current;
    const centeringArea = chartCenteringAreaRef.current;

    if (!hideInlineControls || !info || !centeringArea) {
      setOrbitInfoReservedWidth(0);
      return undefined;
    }

    const updateReservedWidth = () => {
      const squareSize = Math.min(centeringArea.clientWidth, centeringArea.clientHeight);
      const maxReservedWidth = Math.max(0, centeringArea.clientWidth - squareSize);
      const infoRightEdge = info.offsetLeft + info.offsetWidth + 12;
      const nextReservedWidth = Math.min(infoRightEdge, maxReservedWidth);

      setOrbitInfoReservedWidth(previous => (previous === nextReservedWidth ? previous : nextReservedWidth));
    };

    updateReservedWidth();

    const observer = new ResizeObserver(updateReservedWidth);
    observer.observe(info);
    observer.observe(centeringArea);

    return () => observer.disconnect();
  }, [hideInlineControls]);

  const exportChart = useCallback(
    (format: "image/png" | "image/jpeg", fileName: string, width: number) => {
      if (chartRef2.current && canvasRef2.current) {
        const dpr = window.devicePixelRatio || 1;
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = width / dpr;
        tempCanvas.height = width / dpr;
        const tempCtx = tempCanvas.getContext("2d");

        if (tempCtx) {
          const tempChart = new ChartJS(tempCanvas, {
            type: isPolarTrendChecked ? "radar" : "line",
            data: chartData,
            options: {
              ...options,
              maintainAspectRatio: false,
              responsive: false,
              ...(polarTrendMode
                ? {}
                : {
                    elements: {
                      point: {
                        radius: 0,
                        hoverRadius: 0,
                      },
                    },
                  }),
              plugins: {
                ...options.plugins,
                title: {
                  ...options.plugins.title,
                  display: true,
                },
              },
            },
            plugins: [orbitSensorMarkersPlugin],
          });

          if (format === "image/jpeg") {
            tempCtx.save();
            tempCtx.globalCompositeOperation = "destination-over";
            tempCtx.fillStyle = "white";
            tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            tempCtx.restore();
          }

          const dataUrl = tempChart.toBase64Image(format, 1);
          downloadDiagram(dataUrl, fileName);

          tempChart.destroy();
          tempCanvas.remove();
        }
      }
    },
    [chartData, isPolarTrendChecked, polarTrendMode, options],
  );

  useEffect(() => {
    renderChart();
    handleResize();

    return destroyChart;
  }, [renderChart, destroyChart]);

  useEffect(() => {
    resetZoom();
  }, [
    orbitSeries,
    appliedOrbitBinsKey,
    appliedBandpassHz?.lo,
    appliedBandpassHz?.hi,
    eccentricityMode,
    radialClearance,
    rotationDirection,
    showOrbitPoints,
    isPolarTrendChecked,
    polarTrendMode,
    resetZoom,
  ]);

  useEffect(() => {
    let frameId = 0;

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        handleResize();
      });
    });

    if (chartContainerRef.current) {
      observer.observe(chartContainerRef.current);
    }

    handleResize();

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [handleResize]);

  useEffect(() => {
    registerExport?.(format => {
      const fileName = getChartExportFileName(format);
      exportChart(format === "png" ? "image/png" : "image/jpeg", fileName, 1024);
    });

    return () => {
      registerExport?.(null);
    };
  }, [exportChart, registerExport]);

  useEffect(() => {
    const canvas = canvasRef2.current;
    if (!canvas) return;

    const handleMiddleClick = (event: MouseEvent) => {
      if (event.button !== 1) {
        return;
      }

      const clearsPoints = !isZoomed && onMiddleClick != null;
      if (!isZoomed && !clearsPoints) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (event.type !== "mousedown") {
        return;
      }

      if (isZoomed) {
        resetZoom();
        return;
      }

      onMiddleClick?.();
    };

    canvas.addEventListener("mousedown", handleMiddleClick, true);
    canvas.addEventListener("auxclick", handleMiddleClick, true);
    return () => {
      canvas.removeEventListener("mousedown", handleMiddleClick, true);
      canvas.removeEventListener("auxclick", handleMiddleClick, true);
    };
  }, [isZoomed, onMiddleClick, resetZoom]);

  return (
    <StyledWrapper ref={wrapperRef}>
      <FormDialog
        key={`radial-clearance-${radialClearanceDialogKey}`}
        initialValues={radialClearanceFormInitialValues}
        validationSchema={validationSchema}
        message="dialogs.inputData"
        showPrompt={false}
        disabled={false}
        onCancel={handleRadialClearanceCancel}
        onConfirm={handleRadialClearanceConfirm}
        open={isRadialClearanceDialogOpen}
        maxWidth="sm"
      >
        <TextInput name="radialClearance" autoComplete="off" autoFocus />
        <Typography variant="caption" color="textSecondary">
          {formatMessage({ id: "form.validations.radialClearanceHint" }, { Amax: maxA.toFixed(1), units })}
        </Typography>
      </FormDialog>

      <Dialog open={orbitFilterOpen} onClose={handleCloseOrbitFilter} maxWidth="md" fullWidth>
        <DialogTitle>{formatMessage({ id: "pages.scheme.analytics.orbitFilterDialogTitle" })}</DialogTitle>
        <DialogContent>
          {onShowOriginalFormChange ? (
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={draftShowOriginalForm}
                  onChange={event => setDraftShowOriginalForm(event.target.checked)}
                  inputProps={{ "aria-label": formatMessage({ id: "pages.scheme.analytics.orbitShowOriginalForm" }) }}
                />
              }
              label={formatMessage({ id: "pages.scheme.analytics.orbitShowOriginalForm" })}
            />
          ) : null}
          {orbitFilterApplyError ? (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {orbitFilterApplyError}
            </Typography>
          ) : null}

          {orbitBandpassAvailable && orbitSamplingRateHz != null ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {formatMessage({ id: "pages.scheme.analytics.orbitBandpassFilterSection" })}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={draftBandpassEnabled} onChange={handleDraftBandpassEnabledChange} />
                }
                label={formatMessage({ id: "pages.scheme.analytics.orbitBandpassFilterEnable" })}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {formatMessage(
                  { id: "pages.scheme.analytics.orbitBandpassFilterFsHint" },
                  {
                    fs: formatBandpassFreqHz(orbitSamplingRateHz),
                    nyq: formatBandpassFreqHz(orbitSamplingRateHz / 2),
                  },
                )}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  size="small"
                  type="text"
                  inputMode="decimal"
                  label={formatMessage({ id: "pages.scheme.analytics.orbitBandpassFilterLo" })}
                  value={draftBandpassLo}
                  onChange={e => setDraftBandpassLo(e.target.value)}
                  onBlur={handleBandpassLoBlur}
                  disabled={!draftBandpassEnabled}
                  error={bandpassLoOutOfRange}
                  inputProps={
                    orbitBandpassFreqBounds
                      ? {
                          min: orbitBandpassFreqBounds.fMin,
                          max: orbitBandpassFreqBounds.fMax,
                          step: "any",
                        }
                      : undefined
                  }
                />
                <TextField
                  size="small"
                  type="text"
                  inputMode="decimal"
                  label={formatMessage({ id: "pages.scheme.analytics.orbitBandpassFilterHi" })}
                  value={draftBandpassHi}
                  onChange={e => setDraftBandpassHi(e.target.value)}
                  onBlur={handleBandpassHiBlur}
                  disabled={!draftBandpassEnabled}
                  error={bandpassHiOutOfRange}
                  inputProps={
                    orbitBandpassFreqBounds
                      ? {
                          min: orbitBandpassFreqBounds.fMin,
                          max: orbitBandpassFreqBounds.fMax,
                          step: "any",
                        }
                      : undefined
                  }
                />
              </Box>
              {orbitBandpassFreqBounds ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {formatMessage(
                    { id: "pages.scheme.analytics.orbitBandpassFilterFieldRangeHelper" },
                    {
                      fMin: formatBandpassFreqHz(orbitBandpassFreqBounds.fMin),
                      fMax: formatBandpassFreqHz(orbitBandpassFreqBounds.fMax),
                    },
                  )}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {orbitHarmonicFilterDisabledReason === "param3" ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterDisabled" })}
            </Typography>
          ) : null}
          {orbitHarmonicFilterDisabledReason === "sync" ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterDisabledSyncOnly" })}
            </Typography>
          ) : null}

          {!orbitHarmonicFilterDisabled && orbitDesktopComponentRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {formatMessage({ id: "pages.scheme.analytics.orbitDesktopComponentsEmpty" })}
            </Typography>
          ) : null}
          {!orbitHarmonicFilterDisabled && orbitDesktopComponentRows.length > 0 ? (
            <TableContainer sx={{ mt: 1, maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>
                      {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterColumnHarm" })}
                    </TableCell>
                    <TableCell align="right">
                      {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterColumnX" })}
                    </TableCell>
                    <TableCell align="right">
                      {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterColumnY" })}
                    </TableCell>
                    <TableCell align="right">
                      {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterColumnS" })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orbitDesktopComponentRows.map(row => {
                    const fmt = (v: number) => (Number.isFinite(v) ? v.toFixed(2) : "—");
                    const fmtHarm = (v: number) => (Number.isFinite(v) ? String(parseFloat(v.toFixed(4))) : "—");
                    return (
                      <TableRow key={row.binIndex} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={draftOrbitBins.includes(row.binIndex)}
                            onChange={() => handleToggleDraftBin(row.binIndex)}
                            disabled={draftBandpassEnabled}
                          />
                        </TableCell>
                        <TableCell>{fmtHarm(row.harmonicRev)}</TableCell>
                        <TableCell align="right">{fmt(row.ampX)}</TableCell>
                        <TableCell align="right">{fmt(row.ampY)}</TableCell>
                        <TableCell align="right">{fmt(row.s)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearOrbitFilter} sx={{ mr: "auto" }} color="inherit">
            {formatMessage({ id: "form.buttons.clear" })}
          </Button>
          <Button onClick={handleCloseOrbitFilter}>{formatMessage({ id: "form.buttons.cancel" })}</Button>
          <Button variant="contained" onClick={handleApplyOrbitFilter}>
            {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterApply" })}
          </Button>
        </DialogActions>
      </Dialog>

      {polarTrendMode ? (
        <StyledChartWithLegendContainer
          sx={{ flexDirection: "row", alignItems: "stretch", gap: 2, flex: 1, minHeight: 0, width: "100%" }}
        >
          <StyledChartContainer ref={chartContainerRef}>
            <ChartZoomOverview
              overview={overview}
              zoomState={zoomState}
              onPan={panZoom}
              top={hideInlineControls || polarTrendMode ? 12 : 36}
              right={hideInlineControls || polarTrendMode ? 12 : 100}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
              >
                <canvas
                  ref={canvasRef2}
                  role="img"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>
            </Box>
          </StyledChartContainer>
          <StyledLegendRightContainer id={POLAR_TREND_LEGEND_CONTAINER_ID} />
        </StyledChartWithLegendContainer>
      ) : (
        <StyledChartWithLegendContainer sx={{ flexDirection: "column" }}>
          <StyledChartContainer ref={chartContainerRef}>
            <ChartZoomOverview
              overview={overview}
              zoomState={zoomState}
              onPan={panZoom}
              top={hideInlineControls || polarTrendMode ? 12 : 36}
              right={hideInlineControls || polarTrendMode ? 12 : 100}
            />
            <Box
              ref={chartCenteringAreaRef}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                pl: `${orbitInfoReservedWidth}px`,
              }}
            >
              <Box
                ref={chartViewportRef}
                sx={{
                  width: "100%",
                  height: "100%",
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
              >
                <canvas
                  ref={canvasRef2}
                  role="img"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>
            </Box>
            <>
              {hideInlineControls ? null : (
                <Box
                  sx={{
                    position: "absolute",
                    right: 4,
                    top: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    pointerEvents: "auto",
                    zIndex: 11,
                    pb: 1,
                  }}
                >
                  <Box sx={{ mb: 0.5, display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      {formatMessage({ id: "pages.scheme.analytics.rotationDirection" })}
                    </Typography>

                    <ToggleButtonGroup
                      exclusive
                      value={rotationDirection}
                      onChange={handleRotationChange}
                      size="small"
                      sx={{
                        "& .MuiToggleButton-root": {
                          padding: "2px",
                          overflow: "visible",
                        },
                      }}
                    >
                      <ToggleButton value={RotationDirection.COUNTER_CLOCKWISE} disabled={isRotationDirectionLocked}>
                        <Tooltip
                          title={formatMessage({ id: "enums.rotationDirection.counterClockwise" })}
                          arrow
                          disableInteractive
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <RotateLeft sx={rotationDirectionIconSx} />
                          </Box>
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value={RotationDirection.UNDEFINED} disabled={isRotationDirectionLocked}>
                        <Tooltip
                          title={formatMessage({ id: "enums.rotationDirection.undefined" })}
                          arrow
                          disableInteractive
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Remove sx={rotationDirectionIconSx} />
                          </Box>
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value={RotationDirection.CLOCKWISE} disabled={isRotationDirectionLocked}>
                        <Tooltip
                          title={formatMessage({ id: "enums.rotationDirection.clockwise" })}
                          arrow
                          disableInteractive
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <RotateRight sx={rotationDirectionIconSx} />
                          </Box>
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <ToggleButton
                    value="showOrbitPoints"
                    selected={showOrbitPoints}
                    onClick={handleToggleShowOrbitPoints}
                    size="small"
                    sx={{ padding: "2px", mb: 0.5 }}
                  >
                    <Tooltip
                      title={formatMessage({ id: "pages.scheme.analytics.showOrbitPoints" })}
                      arrow
                      disableInteractive
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CommitOutlined />
                      </Box>
                    </Tooltip>
                  </ToggleButton>

                  <ToggleButton
                    value="eccentricity"
                    selected={eccentricityMode}
                    onClick={handleEccentricityToggle}
                    size="small"
                    sx={{ padding: "2px", mb: 0.5 }}
                  >
                    <Tooltip
                      title={formatMessage({ id: "pages.scheme.analytics.showEccentricity" })}
                      arrow
                      disableInteractive
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24 }}
                      >
                        <FormattedMessage id="pages.scheme.analytics.showEccentricityLabel" />
                      </Box>
                    </Tooltip>
                  </ToggleButton>

                  <Tooltip title={orbitFilterTooltip} arrow disableInteractive>
                    <span>
                      <ToggleButton
                        value="orbitHarmonics"
                        selected={orbitFilterOpen}
                        onClick={handleOpenOrbitFilter}
                        size="small"
                        sx={{ padding: "2px", mb: 0.5 }}
                        disabled={orbitFilterButtonDisabled}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <FilterAltOutlined />
                        </Box>
                      </ToggleButton>
                    </span>
                  </Tooltip>
                </Box>
              )}

              <Box
                ref={orbitInfoRef}
                sx={{
                  position: "absolute",
                  left: 4,
                  top: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  pointerEvents: "none",
                  zIndex: 10,
                  pl: 1,
                  pb: 1,
                }}
              >
                <Typography sx={{ fontSize: 12 }}>{`Xmax: ${maxX.toFixed(1)} ${units}`}</Typography>
                <Typography sx={{ fontSize: 12 }}>{`Ymax: ${maxY.toFixed(1)} ${units}`}</Typography>
                <Typography sx={{ fontSize: 12 }}>{`Smax: ${maxS.toFixed(1)} ${units}`}</Typography>
                {orbit?.rotationFrequencyHz != null && orbit.rotationFrequencyHz > 0 ? (
                  <Typography sx={{ fontSize: 12 }}>
                    {formatMessage({ id: "pages.scheme.analytics.orbitRotationFrequency" })}:{" "}
                    {orbit.rotationFrequencyHz.toFixed(2)} Hz
                  </Typography>
                ) : null}
                {appliedBandpassHz ? (
                  <Typography sx={{ fontSize: 12, color: "primary.main" }}>
                    {formatMessage(
                      { id: "pages.scheme.analytics.orbitBandpassFilterActive" },
                      { lo: appliedBandpassHz.lo.toFixed(2), hi: appliedBandpassHz.hi.toFixed(2) },
                    )}
                  </Typography>
                ) : null}
                {appliedOrbitBins.length > 0 ? (
                  <Typography sx={{ fontSize: 12, color: "primary.main" }}>
                    {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterActive" })}
                  </Typography>
                ) : null}
                {eccentricityMode && radialClearance !== null && (
                  <Typography sx={{ fontSize: 12 }}>{`Cr: ${radialClearance.toFixed(1)} ${units}`}</Typography>
                )}
              </Box>
            </>
          </StyledChartContainer>
        </StyledChartWithLegendContainer>
      )}
    </StyledWrapper>
  );
};
